import { Chess, type Square } from 'chess.js';
import type { Color, Dests, Key } from '@lichess-org/chessground/types';

/** Legal move destinations for Chessground from chess.js. */
export function chessDests(chess: Chess): Dests {
  const dests: Dests = new Map();
  const moves = chess.moves({ verbose: true });
  for (const m of moves) {
    const from = m.from as Key;
    let list = dests.get(from);
    if (!list) {
      list = [];
      dests.set(from, list);
    }
    if (!list.includes(m.to as Key)) list.push(m.to as Key);
  }
  return dests;
}

export function chessTurnColor(chess: Chess): Color {
  return chess.turn() === 'w' ? 'white' : 'black';
}

export type EnginePlayMode = 'local' | 'engine-white' | 'engine-black';

/** True when Stockfish should reply (vs engine, game ongoing, not human's turn). */
export function isEngineTurnToPlay(
  playMode: EnginePlayMode,
  turnColor: Color,
  humanColor: Color | null,
  gameOver: boolean
): boolean {
  if (gameOver || playMode === 'local' || humanColor === null) return false;
  return turnColor !== humanColor;
}

export function oppositeColor(c: Color): Color {
  return c === 'white' ? 'black' : 'white';
}

export function needsPromotion(chess: Chess, orig: Key, dest: Key): boolean {
  const piece = chess.get(orig as Square);
  if (!piece || piece.type !== 'p') return false;
  const rank = dest[1];
  return (piece.color === 'w' && rank === '8') || (piece.color === 'b' && rank === '1');
}

export function gameStatusText(chess: Chess): string {
  if (chess.isCheckmate()) {
    const winner = chess.turn() === 'w' ? 'Black' : 'White';
    return `Checkmate — ${winner} wins`;
  }
  if (chess.isStalemate()) return 'Stalemate — draw';
  if (chess.isThreefoldRepetition()) return 'Draw — threefold repetition';
  if (chess.isInsufficientMaterial()) return 'Draw — insufficient material';
  if (chess.isDraw()) return 'Draw';
  if (chess.isCheck()) {
    const side = chess.turn() === 'w' ? 'White' : 'Black';
    return `${side} is in check`;
  }
  const side = chess.turn() === 'w' ? 'White' : 'Black';
  return `${side} to move`;
}

export function formatMoveList(chess: Chess): string[] {
  const verbose = chess.history({ verbose: true });
  const lines: string[] = [];
  for (let i = 0; i < verbose.length; i += 2) {
    const num = Math.floor(i / 2) + 1;
    const w = verbose[i]!.san;
    const b = verbose[i + 1]?.san;
    lines.push(b ? `${num}. ${w} ${b}` : `${num}. ${w}`);
  }
  return lines;
}
